import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import styled, { css } from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import NestableClickableListItem from 'LuxKit/List/NestableClickableListItem'
import productLabels from 'i18n/productLabels'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useRebrandVersion from 'hooks/useRebrandVersion'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import type { BookedHotelSummary, HotelTilePrice } from '../hooks/useTripPlanHotelTileData'
import { PLACEHOLDER_TILE_BORDER_WIDTH } from './constants'
import TripPlanTileDashedOutline, { tripPlanTileOutlineHoverStyles } from './TripPlanTileDashedOutline'

const HOTEL_TILE_ICON_BACKGROUND = 'rgba(110, 79, 230, 0.2)'
const HOTEL_TILE_ICON_COLOUR = '#6E4FE6'

const HotelTileContainer = styled(NestableClickableListItem)`
  border: ${rem(PLACEHOLDER_TILE_BORDER_WIDTH)} solid transparent;
  padding: ${rem(12)} ${rem(14)};
  ${tripPlanTileOutlineHoverStyles}

  &.has-saved-hotels {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.has-saved-hotels:hover,
  &.has-saved-hotels:focus-within {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &&:not(:disabled):hover {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const HotelThumbnail = styled(ListItemThumbnail)<{ $placeholder?: boolean }>`
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(10)};

  ${props => props.$placeholder && css`
    &&& {
      background-color: ${HOTEL_TILE_ICON_BACKGROUND};
      color: ${HOTEL_TILE_ICON_COLOUR};
    }
  `}

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const BookedHotelImageWrapper = styled.div`
  width: ${rem(40)};
  height: ${rem(40)};
  overflow: hidden;
`

const HotelTileActions = styled(Group)`
  flex-shrink: 0;
`

const browseButtonLayout = css`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  white-space: nowrap;
  line-height: 1;
`

const HotelBrowseButton = styled(TextButton)`
  ${browseButtonLayout}
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

/*
 * Deliberately carries no border or padding of its own. The legacy button
 * hard-codes both, which would override LuxKit's secondary styling (its rules
 * sit in zero-specificity `:where()`) and keep the faint border and grey hover
 * the rebrand is meant to remove.
 */
const HotelBrowseButtonRebrand = styled(TextButton)`
  ${browseButtonLayout}
`

interface Props {
  bookedHotelSummary?: BookedHotelSummary
  hasSavedHotels: boolean
  hotelTilePrice?: HotelTilePrice
  onActionsClick: () => void
  onOpen: () => void
  savedHotelCount: number
}

function TripPlanHotelTile({
  bookedHotelSummary,
  hasSavedHotels,
  hotelTilePrice,
  onActionsClick,
  onOpen,
  savedHotelCount,
}: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  /*
   * Rebrand replaces the low-contrast tertiary search button with a secondary
   * "View offer" button on desktop, and a tertiary search icon button on mobile.
   */
  const isRebrand = useRebrandVersion() === 'rebrand'
  const { viewOfferCopy } = useTripPlannerCTALabels()

  const hotelsLabel = intl.formatMessage(productLabels.hotels)

  const mobileSearchLabel = intl.formatMessage({
    id: 'g0txbn0Fo4rljTc7oksG',
    defaultMessage: 'Search',
    description: 'Button label to submit hotel or experience search refinements in the trip planner drawer',
  })

  const searchHotelsLabel = intl.formatMessage({
    id: 'MIObslnviv0W_jIU8tqL',
    defaultMessage: 'Search hotels',
    description: 'Button label to open the hotel offers/search drawer from the trip planner stop details',
  })

  const browseOffersLabel = isMobile ? mobileSearchLabel : searchHotelsLabel

  const hotelTilePriceLabel = hotelTilePrice === undefined ? undefined : intl.formatMessage(
    {
      id: 'BW9zGX4U8wuVNhip8PV5',
      defaultMessage: 'From {price}/night',
      description: 'Per-night hotel price copy shown on the trip planner stop hotel tile',
    },
    { price: <FormatCurrency value={hotelTilePrice.price} currency={hotelTilePrice.currency} format="roundedDollar" /> },
  )

  const canOpenBookedHotelDetails = !!bookedHotelSummary?.offerId
  const showBrowseAction = !bookedHotelSummary && !hasSavedHotels

  const hotelActionsLabel = canOpenBookedHotelDetails ? intl.formatMessage({
    id: 'er3KYQL--1nG7z4UFgVM',
    defaultMessage: 'View booked hotel details',
    description: 'Accessible label for the booked hotel summary action button that opens hotel details',
  }) : intl.formatMessage({
    id: '-kGlhMerE6iu-aaslfxi',
    defaultMessage: 'Browse hotel offers',
    description: 'Accessible label for the hotel empty state overflow action button that opens hotel offers',
  })

  const savedHotelsLabel = intl.formatMessage(
    {
      id: 'cUvlPRjyyculVBKmyRau',
      defaultMessage: '{count, plural, one {# saved} other {# saved}}',
      description: 'Status badge showing the number of saved hotels in a trip planner stop',
    },
    { count: savedHotelCount },
  )

  const bookedHotelNightsLabel = bookedHotelSummary?.nights === undefined ? undefined : intl.formatMessage(
    {
      id: 'LNKvL3HZkcGvajZC7thu',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Night count shown under a booked hotel in the trip planner stop hotel tile',
    },
    { count: bookedHotelSummary.nights },
  )

  return (
    <HotelTileContainer
      className={clsx({ 'has-saved-hotels': hasSavedHotels || !!bookedHotelSummary })}
      onClick={onOpen}
      size="medium"
      title={bookedHotelSummary?.name ?? hotelsLabel}
      titleWeight="bold"
      titleLineClamp={1}
      subtitle={bookedHotelSummary ? bookedHotelNightsLabel : hotelTilePriceLabel}
      subtitleLineClamp={1}
      startIcon={
        <HotelThumbnail $placeholder={!bookedHotelSummary} kind={bookedHotelSummary ? 'neutral' : 'highlight-secondary'} gutter="none">
          {bookedHotelSummary?.imageId && (
            <BookedHotelImageWrapper>
              <ResponsiveImage aspectRatio="1:1" id={bookedHotelSummary.imageId} fit="center" alt={bookedHotelSummary.name} />
            </BookedHotelImageWrapper>
          )}
          {!bookedHotelSummary?.imageId && (
            <LineBedIcon size="XS" />
          )}
        </HotelThumbnail>
      }
    >
      {!hasSavedHotels && !bookedHotelSummary && <TripPlanTileDashedOutline radius="S" />}
      <HotelTileActions direction="horizontal" gap={8} verticalAlign="center">
        {bookedHotelSummary && <BookedLabel />}
        {!bookedHotelSummary && hasSavedHotels && (
          <MessagingLabel variant="success">
            {savedHotelsLabel}
          </MessagingLabel>
        )}
        {showBrowseAction && isRebrand && isMobile && (
          <IconButton size="small" kind="tertiary" onClick={onOpen} aria-label={searchHotelsLabel}>
            <LineSearchIcon />
          </IconButton>
        )}
        {showBrowseAction && isRebrand && !isMobile && (
          <HotelBrowseButtonRebrand size="small" kind="secondary" shape="round" onClick={onOpen}>
            {viewOfferCopy}
          </HotelBrowseButtonRebrand>
        )}
        {showBrowseAction && !isRebrand && (
          <HotelBrowseButton size="small" kind="tertiary" onClick={onOpen} startIcon={<LineSearchIcon size="XS" />}>
            {browseOffersLabel}
          </HotelBrowseButton>
        )}
        <IconButton
          size="small"
          kind="tertiary"
          onClick={onActionsClick}
          aria-label={hotelActionsLabel}
        >
          <LineEllipsisVIcon />
        </IconButton>
      </HotelTileActions>
    </HotelTileContainer>
  )
}

export default TripPlanHotelTile
